import type { AccrualZeroReason, CommissionCell, PaymentBasis } from '@/types/api'

/** Five years is a cap, not a term. Past it a payment accrues zero, with the reason recorded. */
export const CONTRACT_YEARS = [1, 2, 3, 4, 5] as const

/** Two kinds of money, because the grid asks one question of monthly and annual alike. */
export const BILLING_KINDS: readonly PaymentBasis[] = ['OneTime', 'Recurring']

/**
 * The fallback: the rate any product with no row of its own is paid at.
 *
 * Spelled out rather than left as a bare `null` at every call site, because the null is
 * load-bearing - it is what makes a grid that lists three products still have an answer for
 * the fourth.
 */
export const FALLBACK = null

/** Where one cell lives in the form. The product is part of the address, not a filter on it. */
export function cellKey(
  year: number,
  kind: PaymentBasis,
  productId: string | null = FALLBACK,
): string {
  return `${productId ?? '*'}:${year}:${kind}`
}

/** Every product the grid names, in the order they were added. The fallback is not one. */
export function productsOf(rates: readonly CommissionCell[] | undefined): string[] {
  return [...new Set((rates ?? []).map((rate) => rate.productId).filter((id) => id !== null))]
}

/**
 * The cells nobody has answered, across the fallback and every product row.
 *
 * <p>
 * A blank is not a zero. Zero means <i>not entitled</i> and is typed rather than assumed:
 * a blank that silently meant zero would make a partner's entitlement depend on somebody
 * having got bored halfway down a column. The API refuses to activate a plan with one, and
 * the editor says so before the click rather than after it.
 * </p>
 * <p>
 * A product is on the grid with all ten of its cells or none. Six of ten would mean the other
 * four silently fell back to the standard rate, which is the same ambiguity read the other way
 * round - so a half-filled product row is reported here rather than quietly accepted.
 * </p>
 */
export function missingCells(
  values: Readonly<Record<string, string>>,
  productIds: readonly string[] = [],
): { year: number; kind: PaymentBasis; productId: string | null }[] {
  return [FALLBACK, ...productIds].flatMap((productId) =>
    CONTRACT_YEARS.flatMap((year) =>
      BILLING_KINDS.filter(
        (kind) => (values[cellKey(year, kind, productId)] ?? '').trim() === '',
      ).map((kind) => ({ year, kind, productId })),
    ),
  )
}

/** The same question against a saved plan rather than a form. */
export function missingRates(
  rates: readonly CommissionCell[],
): { year: number; kind: PaymentBasis; productId: string | null }[] {
  return [FALLBACK, ...productsOf(rates)].flatMap((productId) =>
    CONTRACT_YEARS.flatMap((year) =>
      BILLING_KINDS.filter(
        (kind) =>
          !rates.some(
            (rate) =>
              rate.contractYear === year &&
              rate.billingKind === kind &&
              (rate.productId ?? null) === productId,
          ),
      ).map((kind) => ({ year, kind, productId })),
    ),
  )
}

/**
 * A replacement plan starts from the grid in force.
 *
 * Retyping ten cells to change one invites a typo, and a typo here is a partner paid the
 * wrong amount for five years. Product rows are seeded with the fallback too, so adding a
 * partner's fourth product starts from the standard rate rather than from nothing.
 */
export function seedGrid(rates: readonly CommissionCell[] | undefined): Record<string, string> {
  const values: Record<string, string> = {}
  for (const productId of [FALLBACK, ...productsOf(rates)]) {
    for (const year of CONTRACT_YEARS) {
      for (const kind of BILLING_KINDS) {
        const found = rates?.find(
          (rate) =>
            rate.contractYear === year &&
            rate.billingKind === kind &&
            (rate.productId ?? null) === productId,
        )
        values[cellKey(year, kind, productId)] = found ? found.percent.toFixed(2) : ''
      }
    }
  }
  return values
}

/** Ten more cells for a product, seeded from what the fallback already says. */
export function seedProduct(
  values: Readonly<Record<string, string>>,
  productId: string,
): Record<string, string> {
  const added: Record<string, string> = { ...values }
  for (const year of CONTRACT_YEARS) {
    for (const kind of BILLING_KINDS) {
      added[cellKey(year, kind, productId)] = values[cellKey(year, kind)] ?? ''
    }
  }
  return added
}

/** Takes a product row back off the grid. What is left falls back to the standard rate. */
export function dropProduct(
  values: Readonly<Record<string, string>>,
  productId: string,
): Record<string, string> {
  const kept: Record<string, string> = {}
  for (const [key, value] of Object.entries(values)) {
    if (!key.startsWith(`${productId}:`)) kept[key] = value
  }
  return kept
}

/** The grid as the API wants it: ten cells for the fallback, ten more for each product. */
export function gridToRates(
  values: Readonly<Record<string, string>>,
  productIds: readonly string[] = [],
): CommissionCell[] {
  return [FALLBACK, ...productIds].flatMap((productId) =>
    CONTRACT_YEARS.flatMap((year) =>
      BILLING_KINDS.map((kind) => ({
        contractYear: year,
        billingKind: kind,
        percent: Number(values[cellKey(year, kind, productId)]),
        productId,
      })),
    ),
  )
}

/**
 * The last four digits of whatever a change request proposes.
 *
 * <p>
 * What the approver is asked to retype, and the same four they have just read down the
 * telephone. Returns an empty string when the summary carries no account number - a
 * profile correction moves no money, and asking for a confirmation on one would train
 * people to type past the confirmation that matters.
 * </p>
 */
export function proposedLastFour(summary: string): string {
  const digits = summary.replace(/\D/g, '')
  return digits.length >= 4 ? digits.slice(-4) : ''
}

const ZERO_REASONS: Record<AccrualZeroReason, string> = {
  RateIsZero: 'rate is zero',
  BeyondFiveYearCap: 'past year 5',
  NoRatesFrozen: 'no rates frozen',
  NoAnchorDate: 'no anchor date',
}

/**
 * Why an accrual came out at nothing.
 *
 * A zero is shown with its reason rather than omitted: an absent row is silence, and
 * silence is what generates the email to finance.
 */
export function zeroReasonLabel(reason: AccrualZeroReason | null): string {
  return reason ? `0.00 · ${ZERO_REASONS[reason]}` : '0.00'
}
